import React, {useState, useEffect, useRef} from "react";
import './css/Analysis.css'; // Corrected path
import Plotly from 'plotly.js-dist';
import MintDBLoader from "./MintDBLoader.tsx";
import FrequencyMap from "./FrequencyMap.tsx";
import DrugTargetMindmap from "./DrugTargetMindmap.tsx";
import DrugGeneMatrix from "./DrugGeneMatrix.tsx";
import MutationBarChart from "./MutationBarChart.tsx";
import MintDBTree from "./MintDBTree.tsx";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

function Analysis(){
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [firstRun, setFirstRun] = useState(true);
    const [query, setQuery] = useState("SELECT DISTINCT t.target_name, t.gene_name, t.pdb_id,t.uniprot_id,d.drug_name,dis.name as disorder_name,r.rsid,r.uk_biobank FROM targets t JOIN drugs d ON t.drug_id = d.id JOIN disorders dis ON d.disorder_id = dis.id LEFT JOIN rsids r ON t.id = r.target_id WHERE dis.name = 'adhd' AND t.gene_name = 'TAAR1' ORDER BY d.drug_name, r.rsid LIMIT 3");
    const [data, setData] = useState<{"columns": string[], "results": any[]}>({"columns":[],"results":[]});
    const [resultsAcknowledged, setResultsAcknowledged] = useState(false);
    const [isAIGeneratedResults, setIsAIGeneratedResults] = useState(false);
    // Tracks which specific "View biobank RSIDs" button is loading, so a single
    // click doesn't put every card's button into the loading state at once.
    const [loadingBiobankKey, setLoadingBiobankKey] = useState<string | null>(null);
    const [disorders, setDisorders] = useState<any[]>([]);
    const [drugs, setDrugs] = useState<any[]>([]);
    const [genes, setGenes] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingQuery, setLoadingQuery] = useState(false);
    const [useQueryBuilder, setUseQueryBuilder] = useState(false);
    const [selectedDisorder, setSelectedDisorder] = useState('');
    const [selectedDrug, setSelectedDrug] = useState('');
    const [selectedGene, setSelectedGene] = useState('');
    const [selectedRsid, setSelectedRsid] = useState('');
    const [selectedBiobank, setSelectedBiobank] = useState('');
    const [selectedClassification, setSelectedClassification] = useState('');
    const [queryError, setQueryError] = useState('');
    const [aiPrompt, setAiPrompt] = useState('');
    const [generatingSQL, setGeneratingSQL] = useState(false);
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
    const [showDatabaseSchema, setShowDatabaseSchema] = useState(false);

    // Sidebar navigation state
    const [activeSection, setActiveSection] = useState<'database-query' | 'target-disorder' | 'drug-gene' | 'rsid-count' | 'mintdb-tree'>('database-query');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(50);

    const [analysisTarget, setAnalysisTarget] = useState('');
    const [analysisDisorder, setAnalysisDisorder] = useState('');
    const [analysisClassification] = useState('');
    const [analysisAlphaMissense, setAnalysisAlphaMissense] = useState('');
    const [analysisParameter, setAnalysisParameter] = useState('uk_biobank');
         const [analysisResults, setAnalysisResults] = useState<{
         target: string;
         disorder: string;
         mode: 'target-only' | 'disorder-only' | 'combined' | '';
         disorders: Array<{
             disorder: string;
             totalRsids: number;
             rsidsInBiobank: number;
             rsidsNotInBiobank: number;
             biobankCoverage: string;
             data: any;
             biobankRsids: string[];
         }>;
         targets: Array<{
             gene_name: string;
             target_name: string;
             uniprot_id: string;
             pdb_id: string;
             rsid_count: number;
             biobank_rsid_count: number;
             biobank_coverage: number;
             analysis?: any;
         }>;
     }>({
         target: '',
         disorder: '',
         mode: '',
         disorders: [],
         targets: []
     });

     // Pagination state for disorder-based analysis
     const [targetPagination, setTargetPagination] = useState({
         offset: 0,
         limit: 3,
         hasMore: false,
         loading: false,
         total: 0
     });

    const [drugCoverageResults, setDrugCoverageResults] = useState<{
        disorders: Array<{
            disorder_id: number;
            disorder_name: string;
            drug_count: number;
        }>;
        total_disorders: number;
        total_drugs: number;
    }>({
        disorders: [],
        total_disorders: 0,
        total_drugs: 0
    });

    const [plotData, setPlotData] = useState([{
        x: ['Total RSIDs', 'In Biobank', 'Not in Biobank'],
        y: [0, 0, 0],
        type: 'bar',
        marker: {
            color: ['#007bff', '#28a745', '#dc3545']
        }
    }]);

    const [runningAnalysis, setRunningAnalysis] = useState(false);
    const [rsidCountSelectedGenes, setRsidCountSelectedGenes] = useState<string[]>([]);
    const [showFrequencyMap, setShowFrequencyMap] = useState(false); // Add state to control visibility
    const plotRef = useRef<HTMLDivElement>(null);
    const disclaimerRef = useRef<HTMLDivElement>(null);

    // Drug Analysis State
    const [selectedDrugForAnalysis, setSelectedDrugForAnalysis] = useState('');
    const [drugAnalysisResults, setDrugAnalysisResults] = useState<{
        drug_name: string;
        disorders: Array<{
            disorder_id: number;
            disorder_name: string;
        }>;
        targets: Array<{
            target_id: number;
            target_name: string;
            gene_name: string;
            uniprot_id: string;
        }>;
    }>({
        drug_name: '',
        disorders: [],
        targets: []
    });
    const [runningDrugAnalysis, setRunningDrugAnalysis] = useState(false);

    const clearData = () => {
        setData({"columns":[],"results":[]})
    }

    const clearQuery = () => {
        setQuery('')
        clearData()
    }

    const runAnalysis = async () => {
        // Determine analysis mode based on selections
        let mode: 'target-only' | 'disorder-only' | 'combined' | '' = '';

        if (analysisParameter === 'drug_coverage') {
            await runDrugCoverageAnalysis();
            return;
        }

        if (analysisTarget && analysisDisorder) {
            mode = 'combined';
        } else if (analysisTarget && !analysisDisorder) {
            mode = 'target-only';
        } else if (!analysisTarget && analysisDisorder) {
            mode = 'disorder-only';
        } else {
            alert('Please select either a target, disorder, or both');
            return;
        }

        setRunningAnalysis(true);

        try {
            if (mode === 'target-only') {
                await runTargetOnlyAnalysis();
            } else if (mode === 'disorder-only') {
                await runDisorderOnlyAnalysis();
            } else if (mode === 'combined') {
                await runCombinedAnalysis();
            }
        } catch (error) {
            console.error('Analysis failed:', error);
            alert('Analysis failed. Please try again.');
        } finally {
            setRunningAnalysis(false);
        }
    };

    const runDrugAnalysis = async () => {
        if (!selectedDrugForAnalysis) {
            alert('Please select a drug');
            return;
        }

        setRunningDrugAnalysis(true);
        
        try {
            // Query for disorders associated with the selected drug
            const disordersQuery = `
                SELECT DISTINCT 
                    d.id as disorder_id,
                    d.name as disorder_name
                FROM disorders d
                INNER JOIN drugs dr ON d.id = dr.disorder_id
                WHERE dr.drug_name = '${selectedDrugForAnalysis}'
                ORDER BY d.name`;
            
            const disordersResult = await executeQuery(disordersQuery);
            
            if (!disordersResult || !disordersResult.results) {
                alert('No disorders found for the selected drug');
                return;
            }

            // Query for targets associated with the selected drug
            const targetsQuery = `
                SELECT DISTINCT ON (t.target_name, t.gene_name, t.uniprot_id)
                    t.id as target_id,
                    t.target_name,
                    t.gene_name,
                    t.uniprot_id
                FROM targets t
                INNER JOIN drugs dr ON t.drug_id = dr.id
                WHERE dr.drug_name = '${selectedDrugForAnalysis}'
                ORDER BY t.target_name, t.gene_name, t.uniprot_id, t.id`;
            
            const targetsResult = await executeQuery(targetsQuery);
            
            if (!targetsResult || !targetsResult.results) {
                alert('No targets found for the selected drug');
                return;
            }

            const newDrugAnalysisResults = {
                drug_name: selectedDrugForAnalysis,
                disorders: disordersResult.results,
                targets: targetsResult.results
            };

            setDrugAnalysisResults(newDrugAnalysisResults);
            
        } catch (error) {
            console.error('Drug analysis failed:', error);
            alert('Drug analysis failed. Please try again.');
        } finally {
            setRunningDrugAnalysis(false);
        }
    };

    const runDrugCoverageAnalysis = async () => {
        try {
            // Query for drug coverage per disorder
            const drugCoverageQuery = `
                SELECT 
                    d.id as disorder_id,
                    d.name as disorder_name,
                    COUNT(DISTINCT dr.id) as drug_count
                FROM disorders d
                LEFT JOIN drugs dr ON d.id = dr.disorder_id
                GROUP BY d.id, d.name
                ORDER BY drug_count DESC, disorder_name ASC`;
            
            const drugCoverageResult = await executeQuery(drugCoverageQuery);
            
            if (drugCoverageResult && drugCoverageResult.results) {
                const disorders = drugCoverageResult.results;
                const totalDisorders = disorders.length;
                console.log(JSON.stringify(disorders))
                const totalDrugs = disorders.reduce((sum, d) => sum + Number(d.drug_count), 0);

                
                const newDrugCoverageResults = {
                    disorders: disorders,
                    total_disorders: totalDisorders,
                    total_drugs: totalDrugs
                };

                console.log(totalDrugs)
                
                setDrugCoverageResults(newDrugCoverageResults);
                setAnalysisResults({ target: '', disorders: [] }); // Clear biobank results
            } else {
                alert('No drug coverage data found');
            }
        } catch (error) {
            console.error('Drug coverage analysis failed:', error);
            throw error;
        }
    };

    const runTargetOnlyAnalysis = async () => {
        // First, find all disorders that have data for the selected target
        const disordersQuery = `
            SELECT DISTINCT d.name as disorder_name
            FROM disorders d
            INNER JOIN rsids r ON d.id = r.disorder_id
            INNER JOIN targets t ON r.target_id = t.id
            WHERE t.gene_name = '${analysisTarget}'
            ORDER BY d.name`;
        
        const disordersResult = await executeQuery(disordersQuery);
        
        if (!disordersResult || !disordersResult.results) {
            alert('No disorders found for the selected target');
            return;
        }

        const disorders = disordersResult.results.map(row => row.disorder_name);
        const allDisorderResults: Array<{
            disorder: string;
            totalRsids: number;
            rsidsInBiobank: number;
            rsidsNotInBiobank: number;
            biobankCoverage: string;
            data: any;
            biobankRsids: string[];
        }> = [];

        // Analyze each disorder
        for (const disorder of disorders) {
            let analysisQuery = `
                SELECT 
                    r.rsid AS "RSID",
                    r.uk_biobank AS "Biobank"
                FROM rsids r
                INNER JOIN mutations m ON r.rsid = m.rsid
                LEFT JOIN disorders d ON r.disorder_id = d.id
                LEFT JOIN targets t ON r.target_id = t.id
                WHERE d.name = '${disorder}' 
                AND t.gene_name = '${analysisTarget}'`;

            // Use AlphaMissense filter if selected, otherwise use classification filter
            if (analysisAlphaMissense) {
                analysisQuery += ` AND m.classification = '${analysisAlphaMissense}'`;
                console.log(`Applied AlphaMissense filter: ${analysisAlphaMissense}`);
            } else if (analysisClassification) {
                analysisQuery += ` AND m.classification = '${analysisClassification}'`;
                console.log(`Applied Classification filter: ${analysisClassification}`);
            }

            analysisQuery += ` GROUP BY r.rsid, r.uk_biobank ORDER BY r.rsid;`;

            console.log(`Final query for disorder ${disorder}:`, analysisQuery);
            const result = await executeQuery(analysisQuery);
            
            if (result && result.results) {
                const data = result.results;
                const totalRsids = data.length;
                const rsidsInBiobank = data.filter(row => row['Biobank'] === 'yes').length;
                const rsidsNotInBiobank = data.filter(row => row['Biobank'] === 'no').length;

                const biobankCoverage = totalRsids > 0 ?
                    Math.round((rsidsInBiobank / totalRsids) * 100) : 0;

                // Filter RSIDs to only include those in Biobank for this disorder
                const biobankRsids = data
                    .filter(row => row['Biobank'] === 'yes')
                    .map(row => row['RSID']);
                 
                allDisorderResults.push({
                    disorder: disorder,
                    totalRsids: totalRsids,
                    rsidsInBiobank: rsidsInBiobank,
                    rsidsNotInBiobank: rsidsNotInBiobank,
                    biobankCoverage: `${biobankCoverage}%`,
                    data: result,
                    biobankRsids: biobankRsids // Store filtered RSIDs for FrequencyMap
                });
            }
        }

        try {
            // Update analysis results with all disorders
            const analysisTitle = analysisTarget + (analysisAlphaMissense ? ` (${analysisAlphaMissense.replace('_', ' ')})` : '');
            const newAnalysisResults = {
                target: analysisTitle,
                disorder: '',
                mode: 'target-only' as const,
                disorders: allDisorderResults.map(d => ({
                    disorder: d.disorder,
                    totalRsids: d.totalRsids,
                    rsidsInBiobank: d.rsidsInBiobank,
                    rsidsNotInBiobank: d.rsidsNotInBiobank,
                    biobankCoverage: d.biobankCoverage,
                    data: d.data,
                    biobankRsids: d.biobankRsids
                })),
                targets: []
            };
            
            setAnalysisResults(newAnalysisResults);

            // Create individual plots for each disorder
            setTimeout(() => {
                allDisorderResults.forEach((disorderResult, index) => {
                    createDisorderPlot(disorderResult, index);
                });
            }, 100);

            // Show frequency map after successful analysis
            setShowFrequencyMap(true);
        } catch (error) {
            console.error('Analysis failed:', error);
            alert('Analysis failed. Please try again.');
        }
    };

    const runDisorderOnlyAnalysis = async () => {
        // Clear previous results and reset pagination
        setAnalysisResults({
            target: '',
            disorder: analysisDisorder,
            mode: 'disorder-only',
            disorders: [],
            targets: []
        });

        setTargetPagination({
            offset: 0,
            limit: 3,
            hasMore: false,
            loading: false,
            total: 0
        });

        // Load first batch of targets
        await loadTargetsForDisorder(0, true);
    };

    const runCombinedAnalysis = async () => {
        try {
            const response = await fetch(`/api/disorders?disorder=${encodeURIComponent(analysisDisorder)}&target=${encodeURIComponent(analysisTarget)}&classification=${encodeURIComponent(analysisClassification || '')}&alphaMissense=${encodeURIComponent(analysisAlphaMissense || '')}`);

            if (!response.ok) {
                throw new Error('Failed to fetch combined analysis data');
            }

            const result = await response.json();

            const newAnalysisResults = {
                target: analysisTarget,
                disorder: analysisDisorder,
                mode: 'combined' as const,
                disorders: [{
                    disorder: result.disorder,
                    totalRsids: result.totalRsids,
                    rsidsInBiobank: result.rsidsInBiobank,
                    rsidsNotInBiobank: result.rsidsNotInBiobank,
                    biobankCoverage: result.biobankCoverage,
                    data: result.data,
                    biobankRsids: result.biobankRsids
                }],
                targets: []
            };

            setAnalysisResults(newAnalysisResults);

            // Create plot for the combined analysis
            setTimeout(() => {
                createDisorderPlot({
                    disorder: result.disorder,
                    totalRsids: result.totalRsids,
                    rsidsInBiobank: result.rsidsInBiobank,
                    rsidsNotInBiobank: result.rsidsNotInBiobank
                }, 0);
            }, 100);

            // Show frequency map for combined analysis
            setShowFrequencyMap(true);

        } catch (error) {
            console.error('Combined analysis failed:', error);
            throw error;
        }
    };

    const loadTargetsForDisorder = async (offset = 0, isInitial = false) => {
        if (!isInitial && targetPagination.loading) {
            return; // Prevent multiple simultaneous loads
        }

        try {
            setTargetPagination(prev => ({ ...prev, loading: true }));

            const response = await fetch(`/api/disorders?disorder=${encodeURIComponent(analysisDisorder)}&offset=${offset}&limit=3`);

            if (!response.ok) {
                throw new Error('Failed to fetch targets for disorder');
            }

            const result = await response.json();

            // Process all 3 targets simultaneously (since we're only loading 3 at a time)
            const analysisPromises = result.targets.map(async (target) => {
                let analysis = null;
                try {
                    const analysisResponse = await fetch(`/api/disorders?disorder=${encodeURIComponent(analysisDisorder)}&target=${encodeURIComponent(target.gene_name)}&classification=${encodeURIComponent(analysisClassification || '')}&alphaMissense=${encodeURIComponent(analysisAlphaMissense || '')}`);

                    if (analysisResponse.ok) {
                        analysis = await analysisResponse.json();
                    }
                } catch (error) {
                    console.warn(`Failed to get analysis for ${target.gene_name}:`, error);
                }

                return {
                    ...target,
                    analysis
                };
            });

            // Wait for all analysis requests to complete
            const targetsWithAnalysis = await Promise.all(analysisPromises);

            setAnalysisResults(prev => ({
                ...prev,
                targets: isInitial ? targetsWithAnalysis : [...prev.targets, ...targetsWithAnalysis]
            }));

            setTargetPagination({
                offset: result.pagination.offset,
                limit: result.pagination.limit,
                hasMore: result.pagination.hasMore,
                loading: false,
                total: result.pagination.total
            });

            // Create plots for the new targets with proper indexing and error handling
            setTimeout(() => {
                try {
                    const currentTargetCount = isInitial ? 0 : analysisResults.targets.length;
                    targetsWithAnalysis.forEach((target, index) => {
                        if (target.analysis) {
                            const plotIndex = currentTargetCount + index;
                            const plotId = `plot-${plotIndex}`;

                            // Check if plot element exists before creating plot
                            const plotElement = document.getElementById(plotId);
                            if (plotElement) {
                                try {
                                    createDisorderPlot({
                                        disorder: target.analysis.disorder,
                                        totalRsids: target.analysis.totalRsids,
                                        rsidsInBiobank: target.analysis.rsidsInBiobank,
                                        rsidsNotInBiobank: target.analysis.rsidsNotInBiobank
                                    }, plotIndex);
                                } catch (plotError) {
                                    console.warn(`Failed to create plot for ${target.gene_name}:`, plotError);
                                }
                            }
                        }
                    });
                } catch (plotError) {
                    console.error('Error creating plots:', plotError);
                }
            }, 500); // Increased timeout to allow DOM to update

        } catch (error) {
            console.error('Error loading targets for disorder:', error);
            setTargetPagination(prev => ({ ...prev, loading: false }));
            // Don't show alert for every error, just log it
            console.warn(`Failed to load targets: ${error.message}`);
        }
    };

    const loadMoreTargets = () => {
        if (!targetPagination.loading && targetPagination.hasMore) {
            loadTargetsForDisorder(targetPagination.offset + targetPagination.limit, false);
        }
    };

    // Cleanup function for Plotly plots
    const cleanupPlots = () => {
        try {
            const plotElements = document.querySelectorAll('[id^="plot-"]');
            plotElements.forEach((element) => {
                try {
                    Plotly.purge(element as HTMLElement);
                } catch (error) {
                    console.warn('Error purging plot:', error);
                }
            });
        } catch (error) {
            console.error('Error during plot cleanup:', error);
        }
    };

    // Cleanup plots when component unmounts or analysis changes
    useEffect(() => {
        return cleanupPlots; // Cleanup on unmount
    }, []);

    // Cleanup plots when switching analysis modes
    useEffect(() => {
        if (analysisResults.mode) {
            cleanupPlots();
        }
    }, [analysisResults.mode]);

    const updatePlot = (data: any, results: any) => {
        const layout = {
            title: `RSID Distribution - ${results.target} (${results.disorder})`,
            yaxis: { 
                title: 'Count', 
                range: [0, Math.max(results.totalRsids + 2, 10)]
            },
            xaxis: {
                title: 'Categories'
            },
            showlegend: false,
            margin: { t: 50, b: 50, l: 50, r: 50 }
        };

        if (plotRef.current) {
            Plotly.newPlot(plotRef.current, data, layout);
        }
    };

    const createDisorderPlot = (disorderResult: any, index: number) => {
        try {
            const plotData = [{
                x: ['Total RSIDs', 'In Biobank'],
                y: [disorderResult.totalRsids, disorderResult.rsidsInBiobank],
                type: 'bar',
                marker: {
                    color: ['#007bff', '#28a745']
                }
            }];

            const layout = {
                title: `${disorderResult.disorder}`,
                yaxis: {
                    title: 'Count',
                    range: [0, Math.max(disorderResult.totalRsids + 2, 10)]
                },
                xaxis: {
                    title: 'Categories'
                },
                showlegend: false,
                margin: { t: 30, b: 30, l: 40, r: 20 },
                autosize: true
            };

            const plotElement = document.getElementById(`plot-${index}`);
            if (plotElement && plotElement.offsetParent !== null) { // Check if element is visible
                // Clear any existing plot first
                if (plotElement.hasChildNodes()) {
                    Plotly.purge(plotElement);
                }

                // Create new plot with error handling
                Plotly.newPlot(plotElement, plotData, layout, {responsive: true, displayModeBar: false})
                    .catch((error) => {
                        console.warn(`Plotly error for plot-${index}:`, error);
                    });
            } else {
                console.warn(`Plot element plot-${index} not found or not visible`);
            }
        } catch (error) {
            console.error(`Error creating plot for index ${index}:`, error);
        }
    };

     const viewBiobankRsidsInResults = async (disorderName: string, biobankRsids: string[], targetName?: string, buttonKey?: string) => {
         if (biobankRsids.length === 0) {
             alert('No biobank RSIDs available for this disorder');
             return;
         }

         try {
             setLoadingQuery(true);
             setLoadingBiobankKey(buttonKey ?? null);
             
             // For large datasets, process in batches to avoid query length limits
             // With 2000 character limit, calculate safe batch size
             const baseQuery = `
                     SELECT DISTINCT ON (r.rsid)
                         r.rsid AS "RSID",
                         t.gene_name AS "Gene Symbol",
                         m.mutation AS "Amino Acid Change",
                         m.position AS "Position",
                         m.classification AS "Classification",
                         r.uk_biobank AS "Biobank Status",
                         m.ensemble_id AS "Ensembl Transcript",
                         d.name AS "Disorder"
                     FROM rsids r
                     LEFT JOIN mutations m ON r.rsid = m.rsid
                     LEFT JOIN targets t ON r.target_id = t.id
                     LEFT JOIN disorders d ON r.disorder_id = d.id
                     WHERE r.rsid IN ()
                     AND d.name = '${disorderName.replace(/'/g, "''")}'
                     ORDER BY r.rsid, m.mutation`;
             
             const baseQueryLength = baseQuery.length;
             // Average RSID length is ~10 chars, plus quotes and comma = ~13 chars per RSID
             // Leave 300 chars buffer for safety and variable RSID lengths
             const availableSpace = 2000 - baseQueryLength - 300;
             const avgRsidLength = 13;
             let batchSize = Math.max(1, Math.floor(availableSpace / avgRsidLength));
             
             // Further reduce batch size if it's still too large (conservative approach)
             if (batchSize > 50) {
                 batchSize = 50; // Cap at 50 RSIDs per batch for safety
             }
             
             console.log(`Base query length: ${baseQueryLength}, Available space: ${availableSpace}, Calculated batch size: ${batchSize}`);
             const batches: string[][] = [];
             
             for (let i = 0; i < biobankRsids.length; i += batchSize) {
                 batches.push(biobankRsids.slice(i, i + batchSize));
             }

             console.log(`Processing ${biobankRsids.length} RSIDs in ${batches.length} batch(es)`);
             
             let allResults: any[] = [];
             let allColumns: string[] = [];

             // Process each batch
             for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
                 const batch = batches[batchIndex];
                 const rsidList = batch.map(rsid => `'${rsid.replace(/'/g, "''")}'`).join(',');
                 
                 const query = `
                     SELECT DISTINCT ON (r.rsid)
                         r.rsid AS "RSID",
                         t.gene_name AS "Gene Symbol",
                         m.mutation AS "Amino Acid Change",
                         m.position AS "Position",
                         m.classification AS "Classification",
                         r.uk_biobank AS "Biobank Status",
                         m.ensemble_id AS "Ensembl Transcript",
                         d.name AS "Disorder"
                     FROM rsids r
                     LEFT JOIN mutations m ON r.rsid = m.rsid
                     LEFT JOIN targets t ON r.target_id = t.id
                     LEFT JOIN disorders d ON r.disorder_id = d.id
                     WHERE r.rsid IN (${rsidList})
                     AND d.name = '${disorderName.replace(/'/g, "''")}'${targetName ? ` AND t.gene_name = '${targetName.replace(/'/g, "''")}'` : ''}${analysisAlphaMissense ? ` AND m.classification = '${analysisAlphaMissense}'` : ''}
                     ORDER BY r.rsid, m.mutation`;

                 console.log(`Processing batch ${batchIndex + 1}/${batches.length} with ${batch.length} RSIDs`);
                 console.log(`Query length: ${query.length} characters`);
                 
                 // Safety check for query length
                 if (query.length > 1950) {
                     console.warn(`Batch ${batchIndex + 1} query too long (${query.length} chars), splitting further`);
                     // If batch is still too big, split it in half and retry
                     const halfSize = Math.floor(batch.length / 2);
                     if (halfSize === 0) {
                         throw new Error(`Cannot process batch - individual RSID creates query too long`);
                     }
                     
                     // Split current batch and add both halves back to processing queue
                     batches.splice(batchIndex + 1, 0, 
                         batch.slice(0, halfSize), 
                         batch.slice(halfSize)
                     );
                     continue; // Skip this iteration and process the smaller batches
                 }
                 
                 const batchResult = await executeQuery(query);
                 
                 if (batchResult && batchResult.results) {
                     if (batchIndex === 0) {
                         allColumns = batchResult.columns || [];
                     }
                     allResults = allResults.concat(batchResult.results);
                     console.log(`Batch ${batchIndex + 1} completed: ${batchResult.results.length} results`);
                 } else if (batchResult && batchResult.error) {
                     // Check if error is due to query length and retry with smaller batch
                     if (batchResult.error.includes('Query too long') && batch.length > 1) {
                         console.warn(`Batch ${batchIndex + 1} failed due to length, retrying with smaller batches`);
                         const halfSize = Math.floor(batch.length / 2);
                         batches.splice(batchIndex + 1, 0, 
                             batch.slice(0, halfSize), 
                             batch.slice(halfSize)
                         );
                         continue;
                     }
                     throw new Error(`Batch ${batchIndex + 1} failed: ${batchResult.error}`);
                 }
             }

             console.log(`Total results collected: ${allResults.length}`);
             
             if (allResults.length > 0) {
                 setIsAIGeneratedResults(false);
                 setData({
                     results: allResults,
                     columns: allColumns
                 });
                 // The Results table lives in the "Database Query" tab, so switch to
                 // it — otherwise setData populates a section that isn't mounted and
                 // it looks like nothing happened.
                 setActiveSection('database-query');
                 // Scroll to results section after the tab has rendered.
                 setTimeout(() => {
                     document.getElementById('query-results-section')?.scrollIntoView({ behavior: 'smooth' });
                 }, 150);
             } else {
                 alert('No data found for the selected biobank RSIDs');
             }
         } catch (error) {
             console.error('Error fetching biobank RSID data:', error);
             alert(`Failed to fetch biobank RSID data: ${error.message || 'Please try again.'}`);
         } finally {
             setLoadingQuery(false);
             setLoadingBiobankKey(null);
         }
     };

    const executeQuery = async (queryString = query) => {
        try {
            const response = await fetch('/api/execute-query', {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ query: queryString })
            });

            const data = await response.json();

            if (!response.ok) {
                return { error: data.error || `HTTP error! status: ${response.status}` };
            }

            return data;
        } catch (error) {
            console.error('Error executing query:', error);
            return { error: 'Network error or invalid response' };
        }
    };

    const generatePDF = () => {
        const doc = new jsPDF();
        let yPosition = 20;

        // Add title
        doc.setFontSize(18);
        doc.text('MintDB Analysis Report', 14, yPosition);
        yPosition += 10;

        // Add date
        doc.setFontSize(10);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPosition);
        yPosition += 15;

        // Add summary statistics
        doc.setFontSize(14);
        doc.text('Summary Statistics', 14, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        const statsData = [
            ['Total Disorders', disorders.length.toString()],
            ['Total Drugs', drugs.length.toString()],
            ['Unique Genes', genes.length.toString()],
            ['Query Results', data.results.length.toString()]
        ];

        autoTable(doc, {
            startY: yPosition,
            head: [['Metric', 'Value']],
            body: statsData,
            theme: 'grid',
            headStyles: { fillColor: [26, 41, 64] }
        });

        yPosition = (doc as any).lastAutoTable.finalY + 15;

        // Add query results if available
        if (data.results.length > 0) {
            doc.setFontSize(14);

            // Check if new page is needed
            if (yPosition > 250) {
                doc.addPage();
                yPosition = 20;
            }

            doc.text('Query Results', 14, yPosition);
            yPosition += 8;

            // Prepare table data
            const tableHead = [data.columns.map(col => col.charAt(0).toUpperCase() + col.slice(1).toLowerCase())];
            const tableBody = data.results.map(row =>
                data.columns.map(col => row[col] !== null && row[col] !== undefined ? String(row[col]) : 'N/A')
            );

            autoTable(doc, {
                startY: yPosition,
                head: tableHead,
                body: tableBody,
                theme: 'striped',
                headStyles: { fillColor: [26, 41, 64] },
                styles: { fontSize: 8 },
                margin: { top: 10 }
            });

            yPosition = (doc as any).lastAutoTable.finalY + 15;
        }

        // Add analysis results if available
        if (analysisResults.disorders.length > 0) {
            if (yPosition > 250 || data.results.length > 0) {
                doc.addPage();
                yPosition = 20;
            }

            doc.setFontSize(14);
            doc.text('Target & Disorder Analysis Results', 14, yPosition);
            yPosition += 8;

            if (analysisResults.mode === 'target-only' || analysisResults.mode === 'combined') {
                for (let index = 0; index < analysisResults.disorders.length; index++) {
                    const disorder = analysisResults.disorders[index];

                    if (index > 0 && yPosition > 220) {
                        doc.addPage();
                        yPosition = 20;
                    }

                    doc.setFontSize(12);
                    doc.text(`Disorder: ${disorder.disorder}`, 14, yPosition);
                    yPosition += 6;

                    const disorderData = [
                        ['Total RSIDs', disorder.totalRsids.toString()],
                        ['In Biobank', disorder.rsidsInBiobank.toString()],
                        ['Not in Biobank', disorder.rsidsNotInBiobank.toString()],
                        ['Coverage', disorder.biobankCoverage]
                    ];

                    autoTable(doc, {
                        startY: yPosition,
                        head: [['Metric', 'Value']],
                        body: disorderData,
                        theme: 'grid',
                        headStyles: { fillColor: [45, 90, 160] },
                        margin: { left: 20 }
                    });

                    yPosition = (doc as any).lastAutoTable.finalY + 10;
                }
            }
        }

        // Add targets from disorder-only analysis
        if (analysisResults.mode === 'disorder-only' && analysisResults.targets.length > 0) {
            if (yPosition > 250) {
                doc.addPage();
                yPosition = 20;
            }

            doc.setFontSize(14);
            doc.text(`Disorder Analysis: ${analysisResults.disorder}`, 14, yPosition);
            yPosition += 8;

            const targetData = analysisResults.targets.map(target => [
                target.gene_name,
                target.target_name,
                target.analysis ? target.analysis.totalRsids.toString() : target.rsid_count.toString(),
                target.analysis ? target.analysis.rsidsInBiobank.toString() : target.biobank_rsid_count.toString(),
                target.analysis ? target.analysis.biobankCoverage : `${target.biobank_coverage}%`
            ]);

            autoTable(doc, {
                startY: yPosition,
                head: [['Gene', 'Target', 'Total RSIDs', 'In Biobank', 'Coverage']],
                body: targetData,
                theme: 'striped',
                headStyles: { fillColor: [45, 90, 160] },
                styles: { fontSize: 9 }
            });

            yPosition = (doc as any).lastAutoTable.finalY + 15;
        }

        // Add drug coverage results if available
        if (drugCoverageResults.disorders.length > 0) {
            doc.addPage();
            yPosition = 20;

            doc.setFontSize(14);
            doc.text('Drug Coverage Analysis', 14, yPosition);
            yPosition += 10;

            const coverageData = drugCoverageResults.disorders.map(disorder => [
                disorder.disorder_name,
                disorder.drug_count.toString()
            ]);

            autoTable(doc, {
                startY: yPosition,
                head: [['Disorder', 'Drug Count']],
                body: coverageData,
                theme: 'striped',
                headStyles: { fillColor: [26, 41, 64] }
            });

            yPosition = (doc as any).lastAutoTable.finalY + 15;
        }

        // Add drug analysis results if available
        if (drugAnalysisResults.drug_name) {
            doc.addPage();
            yPosition = 20;

            doc.setFontSize(14);
            doc.text(`Drug Analysis: ${drugAnalysisResults.drug_name}`, 14, yPosition);
            yPosition += 10;

            // Disorders
            if (drugAnalysisResults.disorders.length > 0) {
                doc.setFontSize(12);
                doc.text('Associated Disorders:', 14, yPosition);
                yPosition += 6;

                const disordersList = drugAnalysisResults.disorders.map(d => [d.disorder_name]);
                autoTable(doc, {
                    startY: yPosition,
                    head: [['Disorder Name']],
                    body: disordersList,
                    theme: 'grid',
                    headStyles: { fillColor: [96, 165, 250] }
                });

                yPosition = (doc as any).lastAutoTable.finalY + 10;
            }

            // Targets
            if (drugAnalysisResults.targets.length > 0) {
                if (yPosition > 200) {
                    doc.addPage();
                    yPosition = 20;
                }

                doc.setFontSize(12);
                doc.text('Associated Targets:', 14, yPosition);
                yPosition += 6;

                const targetsList = drugAnalysisResults.targets.map(t => [
                    t.target_name,
                    t.gene_name || 'N/A',
                    t.uniprot_id || 'N/A'
                ]);

                autoTable(doc, {
                    startY: yPosition,
                    head: [['Target Name', 'Gene Symbol', 'UniProt ID']],
                    body: targetsList,
                    theme: 'striped',
                    headStyles: { fillColor: [96, 165, 250] },
                    styles: { fontSize: 9 }
                });

                yPosition = (doc as any).lastAutoTable.finalY + 10;
            }
        }

        // Save the PDF
        const filename = `MintDB_Analysis_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(filename);
    };

    const generateCSV = () => {
        let csvContent = '';

        // Add header
        csvContent += 'MintDB Analysis Report\n';
        csvContent += `Generated: ${new Date().toLocaleString()}\n\n`;

        // Add summary statistics
        csvContent += 'Summary Statistics\n';
        csvContent += 'Metric,Value\n';
        csvContent += `Total Disorders,${disorders.length}\n`;
        csvContent += `Total Drugs,${drugs.length}\n`;
        csvContent += `Unique Genes,${genes.length}\n`;
        csvContent += `Query Results,${data.results.length}\n\n`;

        // Add query results if available
        if (data.results.length > 0) {
            csvContent += 'Query Results\n';
            csvContent += data.columns.join(',') + '\n';
            data.results.forEach(row => {
                const values = data.columns.map(col => {
                    const value = row[col];
                    if (value === null || value === undefined) return 'N/A';
                    // Escape quotes and wrap in quotes if contains comma
                    const stringValue = String(value);
                    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                        return `"${stringValue.replace(/"/g, '""')}"`;
                    }
                    return stringValue;
                });
                csvContent += values.join(',') + '\n';
            });
            csvContent += '\n';
        }

        // Add analysis results if available
        if (analysisResults.disorders.length > 0) {
            csvContent += 'Target & Disorder Analysis Results\n';

            if (analysisResults.mode === 'target-only' || analysisResults.mode === 'combined') {
                csvContent += 'Disorder,Total RSIDs,In Biobank,Not in Biobank,Coverage\n';
                analysisResults.disorders.forEach(disorder => {
                    csvContent += `${disorder.disorder},${disorder.totalRsids},${disorder.rsidsInBiobank},${disorder.rsidsNotInBiobank},${disorder.biobankCoverage}\n`;
                });
                csvContent += '\n';
            }
        }

        // Add targets from disorder-only analysis
        if (analysisResults.mode === 'disorder-only' && analysisResults.targets.length > 0) {
            csvContent += `Disorder Analysis: ${analysisResults.disorder}\n`;
            csvContent += 'Gene,Target,Total RSIDs,In Biobank,Coverage\n';
            analysisResults.targets.forEach(target => {
                csvContent += `${target.gene_name},${target.target_name},`;
                csvContent += `${target.analysis ? target.analysis.totalRsids : target.rsid_count},`;
                csvContent += `${target.analysis ? target.analysis.rsidsInBiobank : target.biobank_rsid_count},`;
                csvContent += `${target.analysis ? target.analysis.biobankCoverage : target.biobank_coverage + '%'}\n`;
            });
            csvContent += '\n';
        }

        // Add drug coverage results if available
        if (drugCoverageResults.disorders.length > 0) {
            csvContent += 'Drug Coverage Analysis\n';
            csvContent += 'Disorder,Drug Count\n';
            drugCoverageResults.disorders.forEach(disorder => {
                csvContent += `${disorder.disorder_name},${disorder.drug_count}\n`;
            });
            csvContent += '\n';
        }

        // Add drug analysis results if available
        if (drugAnalysisResults.drug_name) {
            csvContent += `Drug Analysis: ${drugAnalysisResults.drug_name}\n\n`;

            if (drugAnalysisResults.disorders.length > 0) {
                csvContent += 'Associated Disorders\n';
                csvContent += 'Disorder Name\n';
                drugAnalysisResults.disorders.forEach(d => {
                    csvContent += `${d.disorder_name}\n`;
                });
                csvContent += '\n';
            }

            if (drugAnalysisResults.targets.length > 0) {
                csvContent += 'Associated Targets\n';
                csvContent += 'Target Name,Gene Symbol,UniProt ID\n';
                drugAnalysisResults.targets.forEach(t => {
                    csvContent += `${t.target_name},${t.gene_name || 'N/A'},${t.uniprot_id || 'N/A'}\n`;
                });
            }
        }

        // Create download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `MintDB_Analysis_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            
            console.log('Fetching initial data...');
            
            const [disordersData, drugsData, genesData] = await Promise.all([
                executeQuery('SELECT DISTINCT id, name FROM disorders'),
                executeQuery('SELECT DISTINCT drug_name FROM drugs'),
                executeQuery("SELECT DISTINCT gene_name FROM targets WHERE gene_name IS NOT NULL AND gene_name != ''")
            ]);

            console.log('Raw responses:');
            console.log('Disorders response:', disordersData);
            console.log('Drugs response:', drugsData);
            console.log('Genes response:', genesData);

            if (disordersData) {
                setDisorders(disordersData.results);
            }
            if (drugsData) {
                setDrugs(drugsData.results);
            }
            if (genesData) {
                setGenes(genesData.results);
                setRsidCountSelectedGenes(genesData.results.map((g: any) => g.gene_name));
            }

            executeCurrentQuery();

            setFirstRun(false);
        } catch (error) {
            console.error('Error fetching initial data:', error);
        } finally {
            setLoading(false);
        }
    };

   const buildQuery = () => {
    if (!useQueryBuilder) return;

    // Helper function to escape single quotes for PostgreSQL
    const escapeString = (str) => str ? str.replace(/'/g, "''") : '';

    let baseQuery = `SELECT DISTINCT
    r.rsid AS "RSID",
    t.gene_name AS "Gene Symbol",
    m.mutation AS "Amino Acid Change",
    m.position AS "Position",
    m.classification AS "Classification",
    r.uk_biobank AS "Biobank Status",
    m.ensemble_id AS "Ensembl Transcript",
    d.name AS "Disorder"
FROM rsids r
LEFT JOIN mutations m ON r.rsid = m.rsid
LEFT JOIN targets t ON r.target_id = t.id
LEFT JOIN disorders d ON r.disorder_id = d.id`;

    let conditions: string[] = [];

    if (selectedDisorder) {
        conditions.push(`d.name = '${escapeString(selectedDisorder)}'`);
    }
    
    if (selectedGene) {
        conditions.push(`t.gene_name = '${escapeString(selectedGene)}'`);
    }
    
    if (selectedRsid) {
        conditions.push(`r.rsid = '${escapeString(selectedRsid)}'`);
    }
    
    if (selectedBiobank && selectedBiobank.trim() !== '') {
        const biobankValue = selectedBiobank.toLowerCase() === 'yes' ? 'yes' : 'no';
        conditions.push(`r.uk_biobank = '${biobankValue}'`);
    }
    
    if (selectedClassification) {
        conditions.push(`m.classification = '${escapeString(selectedClassification)}'`);
    }

    if (conditions.length > 0) {
        baseQuery += '\nWHERE ' + conditions.join(' AND ');
    }

    baseQuery += '\nORDER BY r.rsid';

    setQuery(baseQuery);
};


    const executeCurrentQuery = async () => {
        setLoadingQuery(true);
        setQueryError('');
        try {
            const result = await executeQuery(query);
            if (result) {
                if (result.error) {
                    setQueryError(result.error);
                } else {
                    setIsAIGeneratedResults(false);
                    setData(result);
                }
            }
        } catch (error) {
            console.error('Error executing current query:', error);
            setQueryError('Failed to execute query. Please check your SQL syntax and try again.');
        } finally {
            setLoadingQuery(false);
        }
    };

    const generateSQLFromAI = async (executeAfter = false, promptOverride = null) => {
        if (!user) {
            if (window.confirm('You must be logged in to use the AI feature. Would you like to login now?')) {
                navigate('/login');
            }
            return;
        }

        const promptToUse = promptOverride || aiPrompt;

        if (!promptToUse.trim()) {
            alert('Please enter a description of what you want to query');
            return;
        }

        setGeneratingSQL(true);
        setQueryError('');

        try {
            const response = await fetch('/api/generateSQL', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ userPrompt: promptToUse })
            });

            const result = await response.json();

            if (!response.ok) {
                const message = result.detail
                    ? `${result.error || 'Failed to generate SQL'}: ${result.detail}`
                    : (result.error || 'Failed to generate SQL');
                setQueryError(message);
                return;
            }

            if (result.success && result.sql) {
                setQuery(result.sql);
                setUseQueryBuilder(false);

                // Execute the query automatically if requested
                if (executeAfter) {
                    setTimeout(async () => {
                        setLoadingQuery(true);
                        try {
                            const queryResult = await executeQuery(result.sql);
                            if (queryResult) {
                                if (queryResult.error) {
                                    setQueryError(queryResult.error);
                                } else {
                                    setIsAIGeneratedResults(true);
                                    setData(queryResult);
                                }
                            }
                        } catch (error) {
                            console.error('Error executing query:', error);
                            setQueryError('Failed to execute query. Please check your SQL syntax and try again.');
                        } finally {
                            setLoadingQuery(false);
                        }
                    }, 100);
                }
            } else {
                setQueryError('Failed to generate SQL query');
            }
        } catch (error) {
            console.error('Error generating SQL:', error);
            setQueryError('Failed to generate SQL. Please try again.');
        } finally {
            setGeneratingSQL(false);
        }
    };

    const handleQueryBuilderToggle = () => {
        const newValue = !useQueryBuilder;
        setUseQueryBuilder(newValue);
        
        if (newValue) {
            setSelectedDisorder('');
            setSelectedDrug('');
            setSelectedGene('');
            setSelectedRsid('');
            setSelectedBiobank('');
            setSelectedClassification('');
            buildQuery();
        }
    };

    useEffect(() => {
        if (firstRun) {
            fetchInitialData();
        }
    }, [firstRun]);

    // Check for AI prompt in URL parameters
    useEffect(() => {
        const promptParam = searchParams.get('prompt');
        const autoRun = searchParams.get('autorun');

        if (promptParam && !loading) {
            setAiPrompt(promptParam);
            setUseQueryBuilder(false); // Switch to AI mode

            // Auto-run the query if autorun parameter is set
            if (autoRun === 'true') {
                setTimeout(() => {
                    generateSQLFromAI(true, promptParam);
                }, 1000);
            }
        }
    }, [searchParams, loading]);

    // Handle anchor link scrolling + sidebar section mapping
    useEffect(() => {
        if (location.hash && !loading) {
            if (location.hash === '#database-query') {
                setActiveSection('database-query');
            } else if (location.hash === '#target-disorder-analysis') {
                setActiveSection('target-disorder');
            } else if (location.hash === '#drug-gene-analysis') {
                setActiveSection('drug-gene');
            } else if (location.hash === '#rsid-count') {
                setActiveSection('rsid-count');
            } else if (location.hash === '#mintdb-tree') {
                setActiveSection('mintdb-tree');
            }
            setTimeout(() => {
                const element = document.querySelector(location.hash);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 300);
        }
    }, [location.hash, loading]);

    useEffect(() => {
        if (useQueryBuilder) {
            buildQuery();
        }
    }, [useQueryBuilder, selectedDisorder, selectedDrug, selectedGene, selectedRsid, selectedBiobank, selectedClassification]);

    // Clear target, disorder and AlphaMissense filter when switching to drug coverage analysis
    useEffect(() => {
        if (analysisParameter === 'drug_coverage') {
            setAnalysisTarget('');
            setAnalysisDisorder('');
            setAnalysisAlphaMissense('');
        }
    }, [analysisParameter]);

    // Reset pagination when data changes
    useEffect(() => {
        setCurrentPage(1);
        setResultsAcknowledged(false);
        if (data.results.length > 0) {
            setTimeout(() => {
                disclaimerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 50);
        }
    }, [data.results]);

    // Initialize empty plot when database-query section is active
    useEffect(() => {
        if (plotRef.current && activeSection === 'database-query') {
            const layout = {
                title: 'RSID Distribution',
                yaxis: { title: 'Count', range: [0, 10] },
                xaxis: { title: 'Categories' },
                showlegend: false,
                margin: { t: 50, b: 50, l: 50, r: 50 }
            };

            Plotly.newPlot(plotRef.current, plotData, layout);
        }
    }, [activeSection]);


    if(loading){
        return (
            <MintDBLoader/>
        )
    }
    return (
        <>
        <style>{`
            .analysis-main-inner {
                padding: 1.5rem 2rem;
                max-width: 1400px;
                margin: 0 auto;
            }
            .analysis-tabs {
                display: flex;
                gap: 0;
                border-bottom: 2px solid #e5e7eb;
                margin-bottom: 1.5rem;
            }
            .analysis-tab {
                padding: 0.625rem 1.25rem;
                cursor: pointer;
                font-size: 0.875rem;
                font-weight: 500;
                color: #64748b;
                border: none;
                background: none;
                border-bottom: 2px solid transparent;
                margin-bottom: -2px;
                transition: all 0.15s ease;
                white-space: nowrap;
            }
            .analysis-tab:hover {
                color: #1A2940;
            }
            .analysis-tab.active {
                color: #1A2940;
                font-weight: 600;
                border-bottom-color: #60a5fa;
            }
            @media (max-width: 576px) {
                .analysis-main-inner { padding: 1rem; }
                .analysis-tab { padding: 0.5rem 0.75rem; font-size: 0.8rem; }
            }
        `}</style>

        <div>
            <div className="analysis-main-inner">

            <div className="row align-items-center">
                <div className="col-md-8">
                    <h1 className='mt-4 analysisHeader'>Analysis Dashboard</h1>
                </div>
                <div className="col-md-4 text-end mt-4">
                    <div style={{ display: 'inline-flex', gap: '0.5rem' }}>
                        <button
                            type="button"
                            onClick={generatePDF}
                            style={{
                                backgroundColor: '#f3f4f6',
                                borderColor: '#e5e7eb',
                                color: '#1A2940',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                border: '1px solid #e5e7eb',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#e5e7eb';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#f3f4f6';
                            }}
                        >
                            Export to PDF
                        </button>
                        <button
                            type="button"
                            onClick={generateCSV}
                            style={{
                                backgroundColor: '#f3f4f6',
                                borderColor: '#e5e7eb',
                                color: '#1A2940',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                border: '1px solid #e5e7eb',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#e5e7eb';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#f3f4f6';
                            }}
                        >
                            Export to CSV
                        </button>
                    </div>
                </div>
            </div>

            <div className="analysis-tabs">
                <button
                    className={`analysis-tab ${activeSection === 'database-query' ? 'active' : ''}`}
                    onClick={() => setActiveSection('database-query')}
                >
                    Database Query
                </button>
                <button
                    className={`analysis-tab ${activeSection === 'target-disorder' ? 'active' : ''}`}
                    onClick={() => setActiveSection('target-disorder')}
                >
                    Target & Disorder
                </button>
                <button
                    className={`analysis-tab ${activeSection === 'drug-gene' ? 'active' : ''}`}
                    onClick={() => setActiveSection('drug-gene')}
                >
                    Drug-Gene-Disorder
                </button>
                <button
                    className={`analysis-tab ${activeSection === 'rsid-count' ? 'active' : ''}`}
                    onClick={() => setActiveSection('rsid-count')}
                >
                    RSID Count
                </button>
                <button
                    className={`analysis-tab ${activeSection === 'mintdb-tree' ? 'active' : ''}`}
                    onClick={() => setActiveSection('mintdb-tree')}
                >
                    Tree
                </button>
            </div>
            {activeSection === 'database-query' && (<>
            <div className="row">

                <div className="row mt-3 text-center statsBoxAnalysis">
                    <div className="col"><h1 className='mt-4 statAnalysis'>{data.results.length}</h1>
                        <div className='mt-3'>QUERY RESULTS</div>
                        <div><p className='sssha'><em>Current Query</em></p></div>
                    </div>
                </div>
            </div>
            <div className='row' style={{"marginTop":'6vh'}} id="database-query">
                <h4 className="analysisHeader"><b>Database Query</b></h4>
                <hr></hr>
                <div className="row">
                    <div className='col-12'>
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={useQueryBuilder}
                                        onChange={handleQueryBuilderToggle}
                                        id="useQueryBuilderCheck"
                                    />
                                    <label className="form-check-label" htmlFor="useQueryBuilderCheck" style={{
                                        fontSize: '0.875rem',
                                        color: '#1A2940',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                    }}>
                                        Use Query Builder
                                    </label>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="showAdvancedOptions"
                                        checked={showAdvancedOptions}
                                        onChange={(e) => setShowAdvancedOptions(e.target.checked)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <label
                                        className="form-check-label"
                                        htmlFor="showAdvancedOptions"
                                        style={{
                                            fontSize: '0.875rem',
                                            color: '#1A2940',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            userSelect: 'none'
                                        }}
                                    >
                                        Show Advanced Options (Raw SQL)
                                    </label>
                                </div>
                            </div>
                        </div>

                        {useQueryBuilder && (
                            <div style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                padding: '1.5rem',
                                backgroundColor: '#f9fafb',
                                marginBottom: '1rem'
                            }}>
                                <div className="row">
                                    <div className='col'>
                                        <label style={{
                                            fontSize: '0.875rem',
                                            fontWeight: '600',
                                            color: '#1A2940',
                                            marginBottom: '0.5rem'
                                        }}>Disorder</label>
                                        <select
                                            className="form-select"
                                            aria-label="Select disorder"
                                            value={selectedDisorder}
                                            onChange={(e) => setSelectedDisorder(e.target.value)}
                                        >
                                            <option value="">Select a disorder...</option>
                                            {disorders.map((disorder, index) => (
                                                <option key={disorder.id} value={disorder.name}>
                                                    {disorder.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className='col'>
                                        <label style={{
                                            fontSize: '0.875rem',
                                            fontWeight: '600',
                                            color: '#1A2940',
                                            marginBottom: '0.5rem'
                                        }}>Gene</label>
                                        <select
                                            className="form-select"
                                            aria-label="Select gene"
                                            value={selectedGene}
                                            onChange={(e) => setSelectedGene(e.target.value)}
                                        >
                                            <option value="">Select a gene...</option>
                                            {genes.map((gene, index) => (
                                                <option key={index} value={gene.gene_name}>
                                                    {gene.gene_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className='col'>
                                        <label style={{
                                            fontSize: '0.875rem',
                                            fontWeight: '600',
                                            color: '#1A2940',
                                            marginBottom: '0.5rem'
                                        }}>RSID</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter RSID"
                                            value={selectedRsid}
                                            onChange={(e) => setSelectedRsid(e.target.value)}
                                        />
                                    </div>
                                    <div className='col'>
                                        <label style={{
                                            fontSize: '0.875rem',
                                            fontWeight: '600',
                                            color: '#1A2940',
                                            marginBottom: '0.5rem'
                                        }}>Biobank</label>
                                        <select
                                            className="form-select"
                                            aria-label="Select biobank option"
                                            value={selectedBiobank}
                                            onChange={(e) => setSelectedBiobank(e.target.value)}
                                        >
                                            <option value="">Any</option>
                                            <option value="yes">Yes</option>
                                            <option value="no">No</option>
                                        </select>
                                    </div>
                                    <div className='col'>
                                        <label style={{
                                            fontSize: '0.875rem',
                                            fontWeight: '600',
                                            color: '#1A2940',
                                            marginBottom: '0.5rem'
                                        }}>Classification</label>
                                        <select
                                            className="form-select"
                                            aria-label="Select classification"
                                            value={selectedClassification}
                                            onChange={(e) => setSelectedClassification(e.target.value)}
                                        >
                                            <option value="">Any classification...</option>
                                            <option value="pathogenic">Pathogenic</option>
                                            <option value="benign">Benign</option>
                                            <option value="no prediction">No Prediction</option>
                                        </select>
                                    </div>
                                </div>

                                <div className='row mt-3'>
                                    <div className='col-md-4'>
                                        <button
                                            type="button"
                                            onClick={executeCurrentQuery}
                                            disabled={loadingQuery}
                                            style={{
                                                backgroundColor: '#1A2940',
                                                borderColor: '#1A2940',
                                                color: 'white',
                                                padding: '0.5rem 1.5rem',
                                                borderRadius: '6px',
                                                fontSize: '0.875rem',
                                                fontWeight: '600',
                                                border: 'none',
                                                cursor: loadingQuery ? 'not-allowed' : 'pointer',
                                                opacity: loadingQuery ? 0.6 : 1,
                                                width: '100%',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!loadingQuery) {
                                                    e.currentTarget.style.backgroundColor = '#2a3f5f';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = '#1A2940';
                                            }}
                                        >
                                            {loadingQuery ? '⏳ Executing...' : '▶ Execute Query'}
                                        </button>
                                    </div>
                                    <div className='col-md-4'>
                                        <button
                                            type="button"
                                            onClick={clearQuery}
                                            style={{
                                                backgroundColor: '#f3f4f6',
                                                borderColor: '#e5e7eb',
                                                color: '#64748b',
                                                padding: '0.5rem 1.5rem',
                                                borderRadius: '6px',
                                                fontSize: '0.875rem',
                                                fontWeight: '600',
                                                border: '1px solid #e5e7eb',
                                                cursor: 'pointer',
                                                width: '100%',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#e5e7eb';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = '#f3f4f6';
                                            }}
                                        >
                                            ✗ Clear Query
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="aiPromptInput" style={{
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                color: '#1A2940',
                                marginBottom: '0.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <span>Query Your Database</span>
                                <span
                                    style={{
                                        position: 'relative',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        backgroundColor: '#e5e7eb',
                                        color: '#1A2940',
                                        fontSize: '0.7rem',
                                        fontWeight: '600',
                                        padding: '0.125rem 0.375rem',
                                        borderRadius: '4px',
                                        border: '1px solid #d1d5db',
                                        cursor: 'help'
                                    }}
                                    onMouseEnter={(e) => {
                                        const tooltip = e.currentTarget.querySelector('[data-ai-tooltip]') as HTMLElement;
                                        if (tooltip) tooltip.style.visibility = 'visible';
                                    }}
                                    onMouseLeave={(e) => {
                                        const tooltip = e.currentTarget.querySelector('[data-ai-tooltip]') as HTMLElement;
                                        if (tooltip) tooltip.style.visibility = 'hidden';
                                    }}
                                >
                                    ✨ AI
                                    <div
                                        data-ai-tooltip=""
                                        style={{
                                            visibility: 'hidden',
                                            position: 'absolute',
                                            top: 'calc(100% + 8px)',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            backgroundColor: '#1A2940',
                                            color: '#fff',
                                            fontSize: '0.75rem',
                                            fontWeight: '400',
                                            padding: '0.5rem 0.75rem',
                                            borderRadius: '6px',
                                            width: '260px',
                                            lineHeight: '1.4',
                                            zIndex: 1000,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>⚠️ AI Disclaimer</div>
                                        <div>AI-generated queries may be inaccurate. Always review the generated SQL before executing.</div>
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: '-4px',
                                                left: '50%',
                                                transform: 'translateX(-50%) rotate(45deg)',
                                                width: '8px',
                                                height: '8px',
                                                backgroundColor: '#1A2940'
                                            }}
                                        />
                                    </div>
                                </span>
                            </label>
                            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="aiPromptInput"
                                    placeholder="e.g., 'Show all pathogenic mutations for anxiety disorders' or 'Get all unique gene names'"
                                    value={aiPrompt}
                                    onChange={(e) => setAiPrompt(e.target.value)}
                                    disabled={useQueryBuilder || generatingSQL || loadingQuery}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            generateSQLFromAI(true);
                                        }
                                    }}
                                    style={{
                                        borderColor: '#e5e7eb',
                                        fontSize: '0.875rem'
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => generateSQLFromAI(true)}
                                    disabled={useQueryBuilder || generatingSQL || loadingQuery || !aiPrompt.trim()}
                                    style={{
                                        backgroundColor: '#1A2940',
                                        borderColor: '#1A2940',
                                        color: 'white',
                                        padding: '0.5rem 1.5rem',
                                        borderRadius: '6px',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        border: 'none',
                                        cursor: (useQueryBuilder || generatingSQL || loadingQuery || !aiPrompt.trim()) ? 'not-allowed' : 'pointer',
                                        opacity: (useQueryBuilder || generatingSQL || loadingQuery || !aiPrompt.trim()) ? 0.6 : 1,
                                        whiteSpace: 'nowrap',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!useQueryBuilder && !generatingSQL && !loadingQuery && aiPrompt.trim()) {
                                            e.currentTarget.style.backgroundColor = '#2a3f5f';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#1A2940';
                                    }}
                                >
                                    {generatingSQL ? '⏳ Generating...' : loadingQuery ? '⏳ Running...' : '▶ Run Query'}
                                </button>
                            </div>
                            {!user && (
                                <div style={{
                                    fontSize: '0.75rem',
                                    color: '#6b7280',
                                    marginTop: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <span>🔒</span>
                                    <span>
                                        AI feature requires login.{' '}
                                        <a
                                            href="/login"
                                            style={{
                                                color: '#1A2940',
                                                textDecoration: 'underline',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Login here
                                        </a>
                                    </span>
                                </div>
                            )}
                        </div>

                        {showAdvancedOptions && (
                            <div style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                padding: '1.5rem',
                                backgroundColor: '#f9fafb',
                                marginBottom: '1rem'
                            }}>
                                <div className='row'>
                                    <div className="form-group col-md-12">
                                        <label htmlFor="exampleFormControlTextarea1" style={{
                                            fontSize: '0.875rem',
                                            fontWeight: '600',
                                            color: '#1A2940',
                                            marginBottom: '0.5rem'
                                        }}>
                                            SQL Query
                                        </label>
                                        <textarea
                                            className="form-control"
                                            id="exampleFormControlTextarea1"
                                            rows="8"
                                            value={query}
                                            onChange={(e) => setQuery(e.target.value)}
                                            disabled={useQueryBuilder}
                                            style={{
                                                fontFamily: 'monospace',
                                                fontSize: '0.875rem',
                                                borderColor: '#e5e7eb'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className='row mt-3'>
                                    <div className='col-md-4'>
                                        <button
                                            type="button"
                                            onClick={executeCurrentQuery}
                                            disabled={loadingQuery}
                                            style={{
                                                backgroundColor: '#1A2940',
                                                borderColor: '#1A2940',
                                                color: 'white',
                                                padding: '0.5rem 1.5rem',
                                                borderRadius: '6px',
                                                fontSize: '0.875rem',
                                                fontWeight: '600',
                                                border: 'none',
                                                cursor: loadingQuery ? 'not-allowed' : 'pointer',
                                                opacity: loadingQuery ? 0.6 : 1,
                                                width: '100%',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!loadingQuery) {
                                                    e.currentTarget.style.backgroundColor = '#2a3f5f';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = '#1A2940';
                                            }}
                                        >
                                            {loadingQuery ? '⏳ Executing...' : '▶ Execute Query'}
                                        </button>
                                    </div>
                                    <div className='col-md-4'>
                                        <button
                                            type="button"
                                            onClick={clearQuery}
                                            style={{
                                                backgroundColor: '#f3f4f6',
                                                borderColor: '#e5e7eb',
                                                color: '#64748b',
                                                padding: '0.5rem 1.5rem',
                                                borderRadius: '6px',
                                                fontSize: '0.875rem',
                                                fontWeight: '600',
                                                border: '1px solid #e5e7eb',
                                                cursor: 'pointer',
                                                width: '100%',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#e5e7eb';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = '#f3f4f6';
                                            }}
                                        >
                                            ✗ Clear Query
                                        </button>
                                    </div>
                                    <div className='col-md-4'>
                                        <button
                                            type="button"
                                            onClick={() => setShowDatabaseSchema(!showDatabaseSchema)}
                                            style={{
                                                backgroundColor: showDatabaseSchema ? '#1A2940' : '#f3f4f6',
                                                borderColor: '#e5e7eb',
                                                color: showDatabaseSchema ? 'white' : '#1A2940',
                                                padding: '0.5rem 1.5rem',
                                                borderRadius: '6px',
                                                fontSize: '0.875rem',
                                                fontWeight: '600',
                                                border: '1px solid #e5e7eb',
                                                cursor: 'pointer',
                                                width: '100%',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!showDatabaseSchema) {
                                                    e.currentTarget.style.backgroundColor = '#e5e7eb';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = showDatabaseSchema ? '#1A2940' : '#f3f4f6';
                                            }}
                                        >
                                            {showDatabaseSchema ? '✓' : '📋'} Database Schema
                                        </button>
                                    </div>
                                </div>

                                {showDatabaseSchema && (
                                    <div className="mt-3 p-3" style={{
                                        backgroundColor: '#ffffff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '0.8125rem'
                                    }}>
                                        <h6 style={{ color: '#1A2940', fontWeight: '600', marginBottom: '1rem' }}>Database Schema</h6>

                                        <div className="mb-3">
                                            <div style={{ fontWeight: '600', color: '#1A2940', marginBottom: '0.5rem' }}>disorders</div>
                                            <div style={{ marginLeft: '1rem', color: '#64748b' }}>
                                                <div>• id (INTEGER, PRIMARY KEY)</div>
                                                <div>• name (TEXT)</div>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <div style={{ fontWeight: '600', color: '#1A2940', marginBottom: '0.5rem' }}>drugs</div>
                                            <div style={{ marginLeft: '1rem', color: '#64748b' }}>
                                                <div>• id (INTEGER, PRIMARY KEY)</div>
                                                <div>• drug_name (TEXT)</div>
                                                <div>• disorder_id (INTEGER, FOREIGN KEY → disorders.id)</div>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <div style={{ fontWeight: '600', color: '#1A2940', marginBottom: '0.5rem' }}>targets</div>
                                            <div style={{ marginLeft: '1rem', color: '#64748b' }}>
                                                <div>• id (INTEGER, PRIMARY KEY)</div>
                                                <div>• target_name (TEXT)</div>
                                                <div>• gene_name (TEXT)</div>
                                                <div>• uniprot_id (TEXT)</div>
                                                <div>• pdb_id (TEXT)</div>
                                                <div>• drug_id (INTEGER, FOREIGN KEY → drugs.id)</div>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <div style={{ fontWeight: '600', color: '#1A2940', marginBottom: '0.5rem' }}>rsids</div>
                                            <div style={{ marginLeft: '1rem', color: '#64748b' }}>
                                                <div>• id (INTEGER, PRIMARY KEY)</div>
                                                <div>• rsid (TEXT)</div>
                                                <div>• uk_biobank (TEXT)</div>
                                                <div>• target_id (INTEGER, FOREIGN KEY → targets.id)</div>
                                            </div>
                                        </div>

                                        <div className="mt-3 p-2" style={{ backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                                            <small style={{ color: '#64748b' }}>
                                                <strong>Note:</strong> Use these table and column names when writing SQL queries.
                                                Foreign keys indicate relationships between tables.
                                            </small>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {queryError && (
                            <div className="row mt-2">
                                <div className="col-md-12">
                                    <div className="alert alert-danger" role="alert">
                                        <strong>Query Error:</strong> {queryError}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <hr className='mt-4'></hr>
                <div className='row mt-2'>
                    <h4 id="query-results-section" className="analysisHeader">Results</h4>
                    {data.results.length > 0 && (
                        <div style={{ position: 'relative' }}>
                            {isAIGeneratedResults && !resultsAcknowledged && (
                                <div
                                    ref={disclaimerRef}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: 'rgba(255, 255, 255, 0.92)',
                                        backdropFilter: 'blur(4px)',
                                        WebkitBackdropFilter: 'blur(4px)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        zIndex: 10,
                                        padding: '2rem',
                                        textAlign: 'center',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                    }}
                                >
                                    <div style={{
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: '#1e40af',
                                        background: '#dbeafe',
                                        border: '1px solid #bfdbfe',
                                        padding: '0.25rem 0.75rem',
                                        borderRadius: '999px',
                                        marginBottom: '1rem',
                                        letterSpacing: '0.05em',
                                        textTransform: 'uppercase',
                                    }}>
                                        AI-generated SQL
                                    </div>
                                    <h5 style={{ color: '#1A2940', marginBottom: '0.5rem', fontWeight: 600 }}>
                                        Results retrieved via an AI-generated query
                                    </h5>
                                    <p style={{
                                        color: '#4b5563',
                                        fontSize: '0.875rem',
                                        maxWidth: '480px',
                                        marginBottom: '1.25rem',
                                        lineHeight: 1.5,
                                    }}>
                                        The data below is retrieved directly from the MintDB database.
                                        The SQL query used to obtain it was generated by AI; users are
                                        advised to review the query to confirm it reflects the intended
                                        request before interpreting the results.
                                    </p>
                                    <button
                                        onClick={() => setResultsAcknowledged(true)}
                                        style={{
                                            background: '#1A2940',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '0.5rem 1.25rem',
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        View Results
                                    </button>
                                </div>
                            )}
                            <div style={{
                                filter: (!isAIGeneratedResults || resultsAcknowledged) ? 'none' : 'blur(3px)',
                                pointerEvents: (!isAIGeneratedResults || resultsAcknowledged) ? 'auto' : 'none',
                                userSelect: (!isAIGeneratedResults || resultsAcknowledged) ? 'auto' : 'none',
                            }}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="text-muted">
                                    Showing {Math.min((currentPage - 1) * itemsPerPage + 1, data.results.length)} - {Math.min(currentPage * itemsPerPage, data.results.length)} of {data.results.length} results
                                </span>
                                {data.results.length > itemsPerPage && (
                                    <nav>
                                        <ul className="pagination pagination-sm mb-0">
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link" 
                                                    onClick={() => setCurrentPage(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                >
                                                    Previous
                                                </button>
                                            </li>
                                            {Array.from({ length: Math.ceil(data.results.length / itemsPerPage) }, (_, i) => i + 1)
                                                .filter(pageNum => 
                                                    pageNum === 1 || 
                                                    pageNum === Math.ceil(data.results.length / itemsPerPage) ||
                                                    Math.abs(pageNum - currentPage) <= 2
                                                )
                                                .map((pageNum, index, array) => (
                                                    <React.Fragment key={pageNum}>
                                                        {index > 0 && array[index - 1] !== pageNum - 1 && (
                                                            <li className="page-item disabled">
                                                                <span className="page-link">...</span>
                                                            </li>
                                                        )}
                                                        <li className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                            <button 
                                                                className="page-link" 
                                                                onClick={() => setCurrentPage(pageNum)}
                                                            >
                                                                {pageNum}
                                                            </button>
                                                        </li>
                                                    </React.Fragment>
                                                ))
                                            }
                                            <li className={`page-item ${currentPage === Math.ceil(data.results.length / itemsPerPage) ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link" 
                                                    onClick={() => setCurrentPage(currentPage + 1)}
                                                    disabled={currentPage === Math.ceil(data.results.length / itemsPerPage)}
                                                >
                                                    Next
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                )}
                            </div>
                            <table className="table mx-auto custom-header" style={{fontSize: '16px'}}>
                                <thead>
                                    <tr>
                                        <th scope='col' className="table-headers" style={{fontSize: '18px'}}>#</th>
                                        {data['columns'].map((item,index) =>(
                                            <th className="table-headers" key={index} scope='col' style={{fontSize: '18px'}}>{item.charAt(0).toUpperCase() + item.slice(1).toLowerCase()}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {data['results']
                                        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                        .map((item, rowIndex) => (
                                            <tr key={rowIndex}>
                                                <th className="table-content" scope="row" style={{fontSize: '16px'}}>
                                                    {(currentPage - 1) * itemsPerPage + rowIndex + 1}
                                                </th>
                                                {Object.keys(item).map((key, colIndex) => (
                                                    <td className="table-content" key={`${rowIndex}-${colIndex}`} style={{fontSize: '16px'}}>{item[key]}</td>
                                                ))}
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                            </div>
                        </div>
                    )}
                    {data.results.length === 0 && (
                        <div className="text-center text-muted py-4">
                            No results found. Execute a query to see results.
                        </div>
                    )}
                </div>
            </div>
            </>)}

            {activeSection === 'target-disorder' && (<>
            <div className="row align-items-end mt-4" id="target-disorder-analysis">
                <h4 className="analysisHeader"><b>Target Protein and Disorder Analysis</b></h4>
                <div className="col mt-3">
                    <label htmlFor="targetSelect" className="tpdAnalysisHeader">
                        <b>Target</b>
                        {analysisParameter === 'drug_coverage' && (
                            <span className="text-muted ms-2">(Not required for Drug Coverage analysis)</span>
                        )}
                    </label>
                    <select
                        className="form-select"
                        id="targetSelect"
                        value={analysisTarget}
                        onChange={(e) => setAnalysisTarget(e.target.value)}
                        disabled={analysisParameter === 'drug_coverage'}
                    >
                        <option value="">Select a target...</option>
                        {genes.map((gene, index) => (
                            <option key={index} value={gene.gene_name}>
                                {gene.gene_name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="col mt-3">
                    <label htmlFor="disorderSelect" className="tpdAnalysisHeader">
                        <b>Disorder</b>
                        {analysisParameter === 'drug_coverage' ? (
                            <span className="text-muted ms-2">(Not required for Drug Coverage analysis)</span>
                        ) : (
                            <span className="text-muted ms-2">(Optional - leave empty to analyze all disorders for selected target)</span>
                        )}
                    </label>
                    <select
                        className="form-select"
                        id="disorderSelect"
                        value={analysisDisorder}
                        onChange={(e) => setAnalysisDisorder(e.target.value)}
                        disabled={analysisParameter === 'drug_coverage'}
                    >
                        <option value="">Select a disorder...</option>
                        {disorders.map((disorder, index) => (
                            <option key={disorder.id} value={disorder.name}>
                                {disorder.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="col mt-3">
                    <label htmlFor="alphaMissenseSelect" className="tpdAnalysisHeader">
                        <b>AlphaMissense</b>
                        {analysisParameter === 'drug_coverage' ? (
                            <span className="text-muted ms-2">(Not required for Drug Coverage analysis)</span>
                        ) : (
                            <span className="text-muted ms-2">(Optional filter)</span>
                        )}
                    </label>
                    <select 
                        className="form-select" 
                        id="alphaMissenseSelect"
                        value={analysisAlphaMissense}
                        onChange={(e) => setAnalysisAlphaMissense(e.target.value)}
                        disabled={analysisParameter === 'drug_coverage'}
                    >
                        <option value="">All predictions...</option>
                        <option value="pathogenic">Pathogenic</option>
                        <option value="benign">Benign</option>
                        <option value="ambiguous">Ambiguous</option>
                    </select>
                </div>

                <div className="col">
                    <label htmlFor="parameterSelect" className="tpdAnalysisHeader"><b>Analysis Parameter</b></label>
                                         <select 
                         className="form-select" 
                         id="parameterSelect"
                         value={analysisParameter}
                         onChange={(e) => setAnalysisParameter(e.target.value)}
                     >
                         <option value="uk_biobank">Biobank Coverage</option>
                     </select>
                </div>
                <div className='col'>
                    <div className="form-group">
                        <button 
                            type="button" 
                            className="queryControls runAnalysis form-control"
                            onClick={runAnalysis}
                            disabled={runningAnalysis}
                        >
                            {runningAnalysis ? '⏳ Running...' : '▶ Run Analysis'}
                        </button>
                    </div>
                </div>
            </div>
                         {/* Analysis Results - Dynamic based on mode */}
             {(analysisResults.disorders.length > 0 || analysisResults.targets.length > 0) && (
                 <div className='row'>
                     <h5 className='mt-3 analysisHeader'>
                         {analysisResults.mode === 'target-only' && `Analysis Results for ${analysisResults.target}`}
                         {analysisResults.mode === 'disorder-only' && `Analysis Results for ${analysisResults.disorder} (${analysisResults.targets.length}${targetPagination.hasMore ? '+' : ''} targets)`}
                         {analysisResults.mode === 'combined' && `Analysis Results for ${analysisResults.target} in ${analysisResults.disorder}`}
                     </h5>

                     {/* Target-only and Combined modes show disorders */}
                     {(analysisResults.mode === 'target-only' || analysisResults.mode === 'combined') && analysisResults.disorders.map((disorderResult, index) => (
                         <div key={index} className="col-md-4 mb-4">
                             <div className="card">
                                 <div className="card-header">
                                     <h6 className="mb-0">{disorderResult.disorder}</h6>
                                 </div>
                                 <div className="card-body">
                                     {/* 1st Row - Summary */}
                                     <div className="mb-3">
                                         <h6>Summary</h6>
                                         <table className="table table-sm">
                                             <tbody>
                                                 <tr>
                                                     <td><b>Total RSIDs</b></td>
                                                     <td>{disorderResult.totalRsids}</td>
                                                 </tr>
                                                 <tr>
                                                     <td><b>In Biobank</b></td>
                                                     <td>{disorderResult.rsidsInBiobank}</td>
                                                 </tr>
                                                 <tr>
                                                     <td><b>Coverage</b></td>
                                                     <td>{disorderResult.biobankCoverage}</td>
                                                 </tr>
                                             </tbody>
                                         </table>
                                     </div>
                                     
                                     {/* 2nd Row - Visualization */}
                                     <div className="mb-3">
                                         <h6>Visualization</h6>
                                         <div id={`plot-${index}`} style={{height: '200px', width: '100%'}}></div>
                                     </div>
                                     
                                                                                                                                                         {/* 3rd Row - Frequency Map */}
                                        <div>
                                            <h6>Frequency Map</h6>
                                            <div className="text-left mb-2">
                                                <small className="text-muted" style={{fontSize: '11px'}}>
                                                    Frequency Distribution of Biobank RSIDs for {analysisResults.target} in {disorderResult.disorder}
                                                </small>
                                            </div>
                                            <FrequencyMap
                                                geneName={analysisResults.target}
                                                rsIDs={disorderResult.biobankRsids}
                                            />
                                        </div>
                                       
                                                                               {/* 4th Row - View in Results Button */}
                                        <div className="mt-3">
                                            <button
                                                type="button"
                                                className="btn btn-sm w-100"
                                                style={{backgroundColor: '#1A2940', borderColor: '#1A2940', color: 'white'}}
                                                onClick={() => viewBiobankRsidsInResults(disorderResult.disorder, disorderResult.biobankRsids, analysisResults.target, `disorder-${index}`)}
                                                disabled={disorderResult.biobankRsids.length === 0 || loadingBiobankKey !== null}
                                            >
                                                {loadingBiobankKey === `disorder-${index}`
                                                    ? '⏳ Loading RSIDs...'
                                                    : disorderResult.biobankRsids.length === 0
                                                        ? 'No Biobank RSIDs'
                                                        : `View ${disorderResult.biobankRsids.length} Biobank RSIDs in Results`
                                                }
                                            </button>
                                        </div>
                                 </div>
                             </div>
                         </div>
                     ))}

                     {/* Disorder-only mode shows targets */}
                     {analysisResults.mode === 'disorder-only' && analysisResults.targets.map((targetResult, index) => (
                         <div key={index} className="col-md-4 mb-4">
                             <div className="card">
                                 <div className="card-header">
                                     <h6 className="mb-0">{targetResult.gene_name}</h6>
                                     <small className="text-muted">{targetResult.target_name}</small>
                                 </div>
                                 <div className="card-body">
                                     {/* Summary */}
                                     <div className="mb-3">
                                         <h6>Summary</h6>
                                         <table className="table table-sm">
                                             <tbody>
                                                 <tr>
                                                     <td><b>Total RSIDs</b></td>
                                                     <td>{targetResult.analysis ? targetResult.analysis.totalRsids : targetResult.rsid_count}</td>
                                                 </tr>
                                                 <tr>
                                                     <td><b>In Biobank</b></td>
                                                     <td>{targetResult.analysis ? targetResult.analysis.rsidsInBiobank : targetResult.biobank_rsid_count}</td>
                                                 </tr>
                                                 <tr>
                                                     <td><b>Coverage</b></td>
                                                     <td>{targetResult.analysis ? targetResult.analysis.biobankCoverage : `${targetResult.biobank_coverage}%`}</td>
                                                 </tr>
                                                 {targetResult.uniprot_id && (
                                                     <tr>
                                                         <td><b>UniProt</b></td>
                                                         <td>
                                                             <a
                                                                 href={`https://www.uniprot.org/uniprot/${targetResult.uniprot_id}`}
                                                                 target="_blank"
                                                                 rel="noopener noreferrer"
                                                                 className="text-decoration-none"
                                                             >
                                                                 {targetResult.uniprot_id}
                                                             </a>
                                                         </td>
                                                     </tr>
                                                 )}
                                             </tbody>
                                         </table>
                                     </div>

                                     {/* Visualization */}
                                     {targetResult.analysis && (
                                         <div className="mb-3">
                                             <h6>Visualization</h6>
                                             <div id={`plot-${index}`} style={{height: '200px', width: '100%'}}></div>
                                         </div>
                                     )}

                                     {/* Frequency Map */}
                                     {targetResult.analysis && targetResult.analysis.biobankRsids.length > 0 && (
                                         <div className="mb-3">
                                             <h6>Frequency Map</h6>
                                             <div className="text-left mb-2">
                                                 <small className="text-muted" style={{fontSize: '11px'}}>
                                                     Frequency Distribution of Biobank RSIDs for {targetResult.gene_name} in {analysisResults.disorder}
                                                 </small>
                                             </div>
                                             <FrequencyMap
                                                 geneName={targetResult.gene_name}
                                                 rsIDs={targetResult.analysis.biobankRsids}
                                             />
                                         </div>
                                     )}

                                     {/* View in Results Button */}
                                     {targetResult.analysis && (
                                         <div className="mt-3">
                                             <button
                                                 type="button"
                                                 className="btn btn-sm w-100"
                                                 style={{backgroundColor: '#1A2940', borderColor: '#1A2940', color: 'white'}}
                                                 onClick={() => viewBiobankRsidsInResults(targetResult.analysis.disorder, targetResult.analysis.biobankRsids, targetResult.gene_name, `target-${index}`)}
                                                 disabled={targetResult.analysis.biobankRsids.length === 0 || loadingBiobankKey !== null}
                                             >
                                                 {loadingBiobankKey === `target-${index}`
                                                     ? '⏳ Loading RSIDs...'
                                                     : targetResult.analysis.biobankRsids.length === 0
                                                         ? 'No Biobank RSIDs'
                                                         : `View ${targetResult.analysis.biobankRsids.length} Biobank RSIDs in Results`
                                                 }
                                             </button>
                                         </div>
                                     )}
                                 </div>
                             </div>
                         </div>
                     ))}

                     {/* Load More button for disorder-only mode */}
                     {analysisResults.mode === 'disorder-only' && (
                         <div className="col-12 text-center mb-4">
                             {targetPagination.hasMore ? (
                                 <button
                                     type="button"
                                     className="btn btn-outline-primary"
                                     onClick={loadMoreTargets}
                                     disabled={targetPagination.loading}
                                 >
                                     {targetPagination.loading
                                         ? '⏳ Loading More...'
                                         : `Load More Targets (${analysisResults.targets.length}/${targetPagination.total})`
                                     }
                                 </button>
                             ) : (
                                 <div className="text-muted">
                                     <small>All {targetPagination.total} targets loaded</small>
                                 </div>
                             )}
                         </div>
                     )}

                     {/* FrequencyMap section - under Target Protein and Disorder Analysis */}
                     {showFrequencyMap && (analysisTarget || analysisDisorder) && (
                         <div className='row mt-4'>
                             <div className="col-12">
                                 <h6 style={{ fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#64748b', marginBottom: '1rem' }}>
                                     All RSID Frequency Map - {analysisTarget || 'All Targets'} in {analysisDisorder || 'All Disorders'}{analysisAlphaMissense ? ` (${analysisAlphaMissense.replace('_', ' ')})` : ''}
                                 </h6>
                                 <FrequencyMap geneName={analysisTarget || analysisDisorder} />
                             </div>
                         </div>
                     )}
                 </div>
                          )}

            {/* Drug Coverage Analysis Results */}
            {drugCoverageResults.disorders.length > 0 && (
                <div className='row'>
                    <h5 className='mt-3 analysisHeader'>Drug Coverage Analysis Results</h5>
                    
                    {/* Summary Stats */}
                    <div className="col-12 mb-4">
                        <div className="card">
                            <div className="card-header">
                                <h6 className="mb-0">Summary</h6>
                            </div>
                            <div className="card-body">
                                <div className="row text-center">
                                    <div className="col-md-4">
                                        <h4 className="text-primary">{drugCoverageResults.total_disorders}</h4>
                                        <p className="text-muted mb-0">Total Disorders</p>
                                    </div>
                                    <div className="col-md-4">
                                        <h4 className="text-success">{drugCoverageResults.total_drugs}</h4>
                                        <p className="text-muted mb-0">Total Drugs</p>
                                    </div>
                                    <div className="col-md-4">
                                        <h4 className="text-info">{Math.round(drugCoverageResults.total_drugs / drugCoverageResults.total_disorders * 100) / 100}</h4>
                                        <p className="text-muted mb-0">Avg Drugs per Disorder</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Drug Coverage Details */}
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header">
                                <h6 className="mb-0">Drug Coverage by Disorder</h6>
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th>Disorder ID</th>
                                                <th>Disorder Name</th>
                                                <th>Drug Count</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {drugCoverageResults.disorders.map((disorder, index) => (
                                                <tr key={index}>
                                                    <td>{disorder.disorder_id}</td>
                                                    <td><strong>{disorder.disorder_name}</strong></td>
                                                    <td>
                                                        <span
                                                            className="badge"
                                                            style={{
                                                                backgroundColor: disorder.drug_count > 0 ? '#1A2940' : '#f3f4f6',
                                                                color: disorder.drug_count > 0 ? 'white' : '#6b7280',
                                                                border: '1px solid #e5e7eb'
                                                            }}
                                                        >
                                                            {disorder.drug_count}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Drug Analysis Section - HIDDEN
            <div className="row mt-5" id="drug-analysis">
                <hr className="mt-3"></hr>
                <h4 className="analysisHeader"><b>Drug Analysis</b></h4>
                <p className="text-muted">Select a drug to analyse its associated disorders and visualise target relationships.</p>
                
                <div className="row align-items-end">
                    <div className="col-md-6">
                        <label htmlFor="drugAnalysisSelect" className="tpdAnalysisHeader">
                            <b>Select Drug</b>
                        </label>
                        <select 
                            className="form-select" 
                            id="drugAnalysisSelect"
                            value={selectedDrugForAnalysis}
                            onChange={(e) => setSelectedDrugForAnalysis(e.target.value)}
                        >
                            <option value="">Choose a drug...</option>
                            {drugs.map((drug, index) => (
                                <option key={index} value={drug.drug_name}>
                                    {drug.drug_name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <button 
                            type="button" 
                            className="queryControls runAnalysis form-control"
                            onClick={runDrugAnalysis}
                            disabled={runningDrugAnalysis || !selectedDrugForAnalysis}
                        >
                            {runningDrugAnalysis ? '⏳ Analyzing...' : '🔍 Analyze Drug'}
                        </button>
                    </div>
                </div>
           
                {/* Drug Analysis Results }
        
                {drugAnalysisResults.drug_name && (
                    <div className="row mt-5">
                        <style jsx>{`
                            .drug-title {
                                font-size: 1.5rem;
                                font-weight: 700;
                                color: var(--color-primary-navy, #1A2940);
                                text-align: left;
                                margin-bottom: 1rem;
                                border-bottom: 1px solid var(--color-border-light, #eef2f6);
                                padding-bottom: 0.5rem;
                            }
                            .stats-summary {
                                display: inline-flex;
                                gap: 1.5rem;
                                margin-bottom: 1.5rem;
                                font-size: 0.9rem;
                                color: var(--color-text-steel, #64748b);
                            }
                            .stats-summary span {
                                font-weight: 600;
                            }
                            .section-header {
                                font-size: 1.1rem;
                                font-weight: 700;
                                color: var(--color-primary-navy, #1A2940);
                                margin-bottom: 1rem;
                                border-left: 4px solid var(--color-accent-blue, #60a5fa);
                                padding-left: 1rem;
                            }
                            .disorder-list {
                                list-style: none;
                                padding: 0;
                                margin: 0;
                            }
                            .disorder-item {
                                padding: 0.5rem 0;
                                border-bottom: 1px solid var(--color-border-light, #eef2f6);
                                font-size: 0.95rem;
                                color: var(--color-primary-navy, #1A2940);
                                position: relative;
                                padding-left: 1rem;
                            }
                            .disorder-item:before {
                                content: '•';
                                color: var(--color-accent-blue, #60a5fa);
                                font-weight: bold;
                                position: absolute;
                                left: 0;
                            }
                            .disorder-item:last-child {
                                border-bottom: none;
                            }
                            .targets-table {
                                width: 100%;
                                border-collapse: collapse;
                                font-size: 0.95rem;
                            }
                            .targets-table th {
                                background: var(--color-background-light, #FBFBFC);
                                border: 1px solid var(--color-border-light, #eef2f6);
                                padding: 0.75rem;
                                text-align: left;
                                font-weight: 600;
                                color: var(--color-primary-navy, #1A2940);
                                font-size: 0.9rem;
                            }
                            .targets-table td {
                                border: 1px solid var(--color-border-light, #eef2f6);
                                padding: 0.75rem;
                                color: var(--color-text-dark, #1E2433);
                            }
                            .targets-table tbody tr:hover {
                                background: var(--color-background-light, #FBFBFC);
                                transition: background-color 0.15s ease;
                            }
                            .uniprot-badge {
                                background: var(--color-badge-bg-secondary, #F8FAFC);
                                color: var(--color-text-steel, #64748b);
                                padding: 0.25rem 0.5rem;
                                border-radius: 0.25rem;
                                font-size: 0.8rem;
                                border: 1px solid var(--color-badge-border-secondary, #E2E8F0);
                            }
                            .mindmap-section {
                                text-align: center;
                                margin: 2rem 0;
                                padding: 1.5rem 0;
                                background: linear-gradient(135deg, transparent 0%, rgba(96, 165, 250, 0.02) 100%);
                            }
                        `}</style>
                        
                        <div className="col-12">
                            {/* Drug Title }
                            <h3 className="drug-title">{drugAnalysisResults.drug_name}</h3>
                            
                            {/* Stats Summary }
                            <div className="stats-summary">
                                <div><span>{drugAnalysisResults.disorders.length}</span> disorders</div>
                                <div><span>{drugAnalysisResults.targets.length}</span> targets</div>
                            </div>

                            <div className="row">
                                {/* Disorders Section}
                                <div className="col-md-4 mb-4">
                                    <h4 className="section-header">Associated Disorders</h4>
                                    {drugAnalysisResults.disorders.length > 0 ? (
                                        <ul className="disorder-list">
                                            {drugAnalysisResults.disorders.map((disorder, index) => (
                                                <li key={index} className="disorder-item">
                                                    {disorder.disorder_name}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-muted">No associated disorders found.</p>
                                    )}
                                </div>

                                {/* Mindmap Section }
                                <div className="col-md-8 mb-4">
                                    <h4 className="section-header">Target Network</h4>
                                    <DrugTargetMindmap 
                                        drugName={drugAnalysisResults.drug_name}
                                        targets={drugAnalysisResults.targets}
                                    />
                                </div>
                            </div>

                            {/* Target Details Table }
                            {drugAnalysisResults.targets.length > 0 && (
                                <div className="mb-4">
                                    <h4 className="section-header">Targets</h4>
                                    <table className="targets-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '50%' }}>Target Name</th>
                                                <th style={{ width: '25%' }}>Gene Symbol</th>
                                                <th style={{ width: '25%' }}>UniProt ID</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {drugAnalysisResults.targets.map((target, index) => (
                                                <tr key={index}>
                                                    <td><strong>{target.target_name}</strong></td>
                                                    <td>{target.gene_name || 'N/A'}</td>
                                                    <td>
                                                        {target.uniprot_id ? (
                                                            <span className="uniprot-badge">{target.uniprot_id}</span>
                                                        ) : (
                                                            'N/A'
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            */
            }

            </>)}

            {activeSection === 'drug-gene' && (<>
            {/* Drug-Gene-Disorder Matrix Visualization */}
            <div className='row mt-4' id="drug-gene-analysis">
                <div className="col-12">
                    <h5 className='analysisHeader mb-3'>Drug-Gene-Disorder Analysis</h5>
                    <DrugGeneMatrix />
                </div>
            </div>
            </>)}

            {activeSection === 'rsid-count' && (<>
            <div className='row mt-4' id="rsid-count">
                <div className="col-12">
                    <h5 className='analysisHeader mb-3'>Unique RSID Count per Gene</h5>
                    <MutationBarChart
                        selectedDisorders={[]}
                        selectedGenes={rsidCountSelectedGenes}
                    />
                </div>
            </div>
            </>)}

            {activeSection === 'mintdb-tree' && (<>
            <div className='row mt-4' id="mintdb-tree">
                <div className="col-12">
                    <h5 className='analysisHeader mb-3'>Tree</h5>
                    <MintDBTree />
                </div>
            </div>
            </>)}

            <hr className="mt-3"></hr>
                </div>
        </div>
        </>
    )
}

export default Analysis;